import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  type User,
  type UserCredential,
  type AuthError,
} from 'firebase/auth';
import { auth } from './config';

// Auth Error Interface
export interface AuthErrorType {
  code: string;
  message: string;
}

// User Profile Interface
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

// Initialize providers
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

// Configure providers
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

// Email/Password Authentication
export const registerWithEmailAndPassword = async (
  email: string,
  password: string,
  displayName?: string
): Promise<UserCredential> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Update profile with display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, {
        displayName: displayName,
      });
    }

    return userCredential;
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

export const loginWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

// Google Authentication
export const loginWithGoogle = async (): Promise<UserCredential> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Extract additional profile data from Google
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const additionalUserInfo = result.user?.providerData?.[0];
    
    // Store additional profile data for initialization
    if (result.user && additionalUserInfo) {
      (result.user as any)._socialProfileData = {
        provider: 'google',
        providerId: additionalUserInfo.providerId,
        photoURL: additionalUserInfo.photoURL,
        phoneNumber: additionalUserInfo.phoneNumber,
        // Google might provide additional data in the token
        accessToken: credential?.accessToken,
      };
    }
    
    return result;
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

// Apple Authentication
export const loginWithApple = async (): Promise<UserCredential> => {
  try {
    const result = await signInWithPopup(auth, appleProvider);
    
    // Extract additional profile data from Apple
    const credential = OAuthProvider.credentialFromResult(result);
    const additionalUserInfo = result.user?.providerData?.[0];
    
    // Store additional profile data for initialization
    if (result.user && additionalUserInfo) {
      (result.user as any)._socialProfileData = {
        provider: 'apple',
        providerId: additionalUserInfo.providerId,
        photoURL: additionalUserInfo.photoURL,
        phoneNumber: additionalUserInfo.phoneNumber,
        // Apple provides limited data but might have access token
        accessToken: credential?.accessToken,
      };
    }
    
    return result;
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

// Password Reset
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

// Logout
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    throw handleAuthError(error as AuthError);
  }
};

// Get current user profile
export const getCurrentUserProfile = (
  user: User | null
): UserProfile | null => {
  if (!user) return null;

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
};

// Error handling helper
const handleAuthError = (error: AuthError): AuthErrorType => {
  let message = 'An authentication error occurred';

  switch (error.code) {
    case 'auth/user-not-found':
      message = 'No user found with this email address';
      break;
    case 'auth/wrong-password':
      message = 'Incorrect password';
      break;
    case 'auth/email-already-in-use':
      message = 'An account with this email already exists';
      break;
    case 'auth/weak-password':
      message = 'Password should be at least 6 characters';
      break;
    case 'auth/invalid-email':
      message = 'Please enter a valid email address';
      break;
    case 'auth/user-disabled':
      message = 'This account has been disabled';
      break;
    case 'auth/too-many-requests':
      message = 'Too many failed attempts. Please try again later';
      break;
    case 'auth/popup-closed-by-user':
      message = 'Sign-in popup was closed before completion';
      break;
    case 'auth/popup-blocked':
      message = 'Sign-in popup was blocked by your browser';
      break;
    default:
      message = error.message || 'Authentication failed';
  }

  return {
    code: error.code,
    message,
  };
};
